// apps/admin/src/features/billing/ApplyCreditMemoModal.tsx
//
// Phase 8.2 Wave 3 Task 18 — Extracted Apply Credit Memo modal sibling.
//
// vg-binding: CONTEXT:decisions=P8.D-485,P8.D-498
//
//  (manual apply credit memo to an invoice):
//   - Dropdown of merchant's unpaid invoices (status SENT/PARTIAL/OVERDUE),
//     sorted oldest first.
//   - Pre-filled `amount_usd_cents = min(memo.remaining, invoice.due)` —
//     editable down to 1 cent.
//   - Validation: amount ≤ min(remaining, invoice.remaining_due).
//   - Confirm: calls endpoint → CREDIT_MEMO_APPLIED tx_group + invoice
//     subtotal/total recomputed. Fully-covered invoice flips to PAID
//     atomically (BE responsibility — surfaced via success toast).
// Covers goals.
//
// Endpoints consumed:
//   GET  /api/v1/admin/invoices?merchant_id=…&pageSize=50
//        (filter to ISSUED/PARTIAL/OVERDUE on FE — Mongo schema labels)
//   GET  /api/v1/admin/invoices/:id  (read version for optimistic lock)
//   POST /api/v1/admin/credit-memos/:id/apply (Idempotency-Key required)
//
// Test IDs (per task-18 spec):
//   apply-credit-memo-modal, apply-credit-memo-invoice-select,
//   credit-memo-apply-submit, credit-memo-apply-amount, credit-memo-apply-error

import { useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';

import { apiClient } from '@printway/shared/api';
import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  useToast,
} from '@printway/ui';
import { useTranslation } from '../../i18n/useTranslation.js';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface ApiErrorEnvelope {
  ok?: false;
  success?: false;
  error?: {
    code?: string;
    message?: string;
    user_message?: string;
    field_errors?: Record<string, string[] | string>;
  };
}

type LooseClient = {
  GET: (
    path: string,
    init?: { headers?: Record<string, string> },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
  POST: (
    path: string,
    init: { body?: unknown; headers?: Record<string, string> },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
};

function extractApiMessage(payload: unknown, fallback: string): string {
  if (payload && typeof payload === 'object') {
    const env = payload as ApiErrorEnvelope;
    if (env.error?.user_message) return env.error.user_message;
    if (env.error?.message) return env.error.message;
  }
  return fallback;
}

function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `idemp-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function formatUsd(cents: number): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ApplyCreditMemoTarget {
  id: string;
  merchant_id: string;
  remaining_usd_cents: number;
}

interface ApplyInvoiceOption {
  id: string;
  invoice_number: string;
  status: string;
  outstanding_usd_cents: number;
  period_start: string | null;
  /** Optional — only present on GET /invoices/:id (used for optimistic lock). */
  version?: number;
}

export interface ApplyCreditMemoModalProps {
  /** Memo to apply; presence-checked via `open` prop. */
  memo: ApplyCreditMemoTarget | null;
  open: boolean;
  onClose: () => void;
  onApplied?: (payload: { invoiceId: string; amountCents: number }) => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// ELIGIBLE_INVOICE_STATUSES per BE apply handler — invoices in these states
// accept a credit-memo apply. Stored as lowercase per the Mongo schema.
const ELIGIBLE_INVOICE_STATUSES = new Set<string>([
  'issued',
  'sent',
  'partial',
  'overdue',
]);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ApplyCreditMemoModal({
  memo,
  open,
  onClose,
  onApplied,
}: ApplyCreditMemoModalProps): JSX.Element | null {
  const { t } = useTranslation('finance');
  const { toast } = useToast();

  const [invoiceOptions, setInvoiceOptions] = useState<ApplyInvoiceOption[]>([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState('');
  const [amountStr, setAmountStr] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Derived: the currently-selected invoice option (for amount validation).
  const selectedInvoice = useMemo(
    () => invoiceOptions.find((o) => o.id === selectedInvoiceId) ?? null,
    [invoiceOptions, selectedInvoiceId],
  );

  // Pre-fill amount = min(memo.remaining, invoice.outstanding) when the
  // user picks an invoice — "pre-filled remaining_amount editable".
  useEffect(() => {
    if (!memo || !selectedInvoice) return;
    const capped = Math.min(
      memo.remaining_usd_cents,
      selectedInvoice.outstanding_usd_cents,
    );
    if (capped > 0) {
      setAmountStr((capped / 100).toFixed(2));
    }
  }, [memo, selectedInvoice]);

  // Reset state whenever the modal opens for a new memo (or closes).
  useEffect(() => {
    if (!open) return;
    setSelectedInvoiceId('');
    setAmountStr('');
    setError(null);
    setInvoiceOptions([]);
    if (!memo) return;
    // Fetch eligible invoices for the memo's merchant — sorted oldest first
    //  (FIFO settlement convention).
    let cancelled = false;
    setInvoiceLoading(true);
    void (async (): Promise<void> => {
      try {
        const client = apiClient() as unknown as LooseClient;
        const params = new URLSearchParams();
        params.set('merchant_id', memo.merchant_id);
        params.set('pageSize', '50');
        // BE accepts a CSV `status` filter on some routes; we filter
        // client-side to keep behaviour identical across BE shapes.
        const { data, response } = await client.GET(
          `/api/v1/admin/invoices?${params.toString()}`,
        );
        if (!response.ok || cancelled) return;
        // debug-bill iter 7 — admin invoice list envelope is
        //   { data: { rows[], page, limit, total, totalPages } }
        // Older FE code read `data.invoices` which is a Phase 4.1
        // sketch shape that never shipped. We also accept
        // `data.invoices` defensively for the merchant-scope variant.
        const body = data as
          | { data?: { rows?: unknown[]; invoices?: unknown[] } }
          | { rows?: unknown[]; invoices?: unknown[] }
          | undefined;
        const inner =
          body && typeof body === 'object' && 'data' in body && body.data
            ? body.data
            : (body as { rows?: unknown[]; invoices?: unknown[] } | undefined);
        const rows = Array.isArray(inner?.rows)
          ? inner.rows
          : Array.isArray(inner?.invoices)
            ? inner.invoices
            : [];
        const eligible: ApplyInvoiceOption[] = rows
          .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object')
          .filter((r) => ELIGIBLE_INVOICE_STATUSES.has(String(r.status ?? '').toLowerCase()))
          .map((r) => ({
            id: String(r.id ?? r._id ?? ''),
            invoice_number: String(r.invoice_number ?? r.id ?? '?'),
            status: String(r.status ?? ''),
            // BE serializeRow admin-scope emits `remaining_cents`; older
            // contracts also wrote `outstanding_usd_cents` /
            // `remaining_usd_cents`. Accept any of the three.
            outstanding_usd_cents: Number(
              r.outstanding_usd_cents ??
                r.remaining_usd_cents ??
                r.remaining_cents ??
                0,
            ),
            period_start:
              typeof r.period_start === 'string' ? r.period_start : null,
          }))
          .filter((o) => o.id.length === 24)
          // oldest first (FIFO). period_start is the canonical age
          // signal; fall back to id (Mongo ObjectId encodes timestamp).
          .sort((a, b) => {
            const ka = a.period_start ?? a.id;
            const kb = b.period_start ?? b.id;
            return ka.localeCompare(kb);
          });
        if (!cancelled) setInvoiceOptions(eligible);
      } finally {
        if (!cancelled) setInvoiceLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, memo]);

  // Parse + validate the user-entered amount.
  const amountCents = useMemo(() => {
    const parsed = parseFloat(amountStr);
    if (!Number.isFinite(parsed) || parsed <= 0) return 0;
    return Math.round(parsed * 100);
  }, [amountStr]);

  const maxApplyCents = useMemo(() => {
    if (!memo || !selectedInvoice) return 0;
    return Math.min(
      memo.remaining_usd_cents,
      selectedInvoice.outstanding_usd_cents,
    );
  }, [memo, selectedInvoice]);

  //  invariant: amount ≤ min(remaining, invoice.remaining_due).
  const amountTooHigh = amountCents > 0 && amountCents > maxApplyCents;

  const submitDisabled =
    submitting ||
    !selectedInvoiceId ||
    amountCents <= 0 ||
    amountTooHigh ||
    invoiceLoading;

  async function submit(): Promise<void> {
    if (!memo) return;
    if (!selectedInvoiceId) {
      setError(t('credit_memo.apply.errSelectInvoice'));
      return;
    }
    if (amountCents <= 0) {
      setError(t('credit_memo.apply.errAmountPositive'));
      return;
    }
    if (amountTooHigh) {
      setError(t('credit_memo.apply.errAmountTooHigh', { max: formatUsd(maxApplyCents) }));
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const client = apiClient() as unknown as LooseClient;
      // Step 1: fetch invoice to obtain version for optimistic lock.
      const invRes = await client.GET(
        `/api/v1/admin/invoices/${encodeURIComponent(selectedInvoiceId)}`,
      );
      if (!invRes.response.ok) {
        setError(
          extractApiMessage(invRes.error, t('credit_memo.toast.genericError')) ||
            t('credit_memo.apply.errInvoiceNotFound', { id: selectedInvoiceId }),
        );
        return;
      }
      const invBody = invRes.data as
        | { data?: { version?: number; merchant_id?: string } }
        | { version?: number; merchant_id?: string };
      const inv =
        'data' in invBody && invBody.data
          ? invBody.data
          : (invBody as { version?: number; merchant_id?: string });
      if (typeof inv.version !== 'number') {
        setError(t('credit_memo.apply.errMissingVersion'));
        return;
      }
      if (inv.merchant_id && inv.merchant_id !== memo.merchant_id) {
        setError(t('credit_memo.apply.errMerchantMismatch'));
        return;
      }

      // Step 2: POST apply with optimistic lock.
      const body: {
        invoice_id: string;
        expected_invoice_version: number;
        amount_usd_cents: number;
      } = {
        invoice_id: selectedInvoiceId,
        expected_invoice_version: inv.version,
        amount_usd_cents: amountCents,
      };
      const applyRes = await client.POST(
        `/api/v1/admin/credit-memos/${encodeURIComponent(memo.id)}/apply`,
        {
          body,
          headers: { 'Idempotency-Key': newIdempotencyKey() },
        },
      );
      if (!applyRes.response.ok) {
        setError(
          extractApiMessage(applyRes.error, t('credit_memo.toast.genericError')) ||
            `HTTP ${applyRes.response.status}`,
        );
        return;
      }

      // Determine whether BE flipped the invoice to PAID (fully covered) —
      // surfaced via the success toast "fully covered → PAID atomically".
      const payload = applyRes.data as
        | { ok?: true; data?: { invoice_status?: string; fully_paid?: boolean } }
        | { invoice_status?: string; fully_paid?: boolean }
        | undefined;
      const innerPayload =
        payload && typeof payload === 'object' && 'data' in payload && payload.data
          ? payload.data
          : (payload as { invoice_status?: string; fully_paid?: boolean } | undefined);
      const fullyPaid =
        Boolean(innerPayload?.fully_paid) ||
        String(innerPayload?.invoice_status ?? '').toLowerCase() === 'paid' ||
        amountCents === selectedInvoice?.outstanding_usd_cents;

      const invoiceLabel =
        selectedInvoice?.invoice_number ?? selectedInvoiceId.slice(-6);
      toast({
        title: fullyPaid
          ? t('credit_memo.apply.toastPaidTitle')
          : t('credit_memo.apply.toastAppliedTitle'),
        description: fullyPaid
          ? t('credit_memo.apply.toastPaidDesc', {
              invoice: invoiceLabel,
              amount: formatUsd(amountCents),
            })
          : t('credit_memo.apply.toastAppliedDesc', {
              amount: formatUsd(amountCents),
              invoice: invoiceLabel,
            }),
        variant: 'success',
      });
      onApplied?.({ invoiceId: selectedInvoiceId, amountCents });
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('credit_memo.toast.networkErrorDesc'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (!open || !memo) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      className="max-w-lg"
      data-testid="apply-credit-memo-modal"
    >
      <DialogHeader>
        <DialogTitle>
          {t('credit_memo.apply.title', { id: memo.id.slice(-6) })}
        </DialogTitle>
      </DialogHeader>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        className="space-y-4 px-6 py-4"
        data-testid="credit-memo-apply-form"
      >
        <div className="rounded-md border border-pw-border bg-pw-surface/40 p-3 text-xs text-pw-text-muted">
          <div>
            <span className="font-medium text-pw-text">
              {t('credit_memo.apply.merchantLabel')}
            </span>{' '}
            {memo.merchant_id}
          </div>
          <div>
            <span className="font-medium text-pw-text">
              {t('credit_memo.apply.remainingLabel')}
            </span>{' '}
            {formatUsd(memo.remaining_usd_cents)}
          </div>
        </div>

        {/* Invoice select (oldest first) */}
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-pw-text">
            {t('credit_memo.apply.invoiceLabel')} <span className="text-red-600 dark:text-red-300">*</span>
          </span>
          {invoiceLoading ? (
            <p className="text-xs text-pw-text-muted">
              {t('credit_memo.apply.loadingInvoices')}
            </p>
          ) : invoiceOptions.length === 0 ? (
            <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 p-2 text-xs text-amber-800 dark:text-amber-300">
              {t('credit_memo.apply.noInvoicesPrefix')}{' '}
              <a
                href="/billing/invoices/manual-generate"
                className="underline hover:text-amber-900"
              >
                /billing/invoices/manual-generate
              </a>{' '}
              {t('credit_memo.apply.noInvoicesSuffix')}
            </div>
          ) : (
            <select
              className="block w-full rounded-md border border-pw-border bg-pw-bg p-2 text-sm focus:border-pw-primary focus:outline-none focus:ring-2 focus:ring-pw-primary/20"
              value={selectedInvoiceId}
              onChange={(e) => setSelectedInvoiceId(e.target.value)}
              data-testid="apply-credit-memo-invoice-select"
            >
              <option value="">{t('credit_memo.apply.selectInvoicePlaceholder')}</option>
              {invoiceOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.invoice_number} · {o.status} ·{' '}
                  {formatUsd(o.outstanding_usd_cents)} {t('credit_memo.apply.outstanding')}
                  {o.period_start ? ` · ${o.period_start.slice(0, 10)}` : ''}
                </option>
              ))}
            </select>
          )}
        </label>

        {/* Amount — pre-filled to min(remaining, outstanding), editable */}
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-pw-text">
            {t('credit_memo.apply.amountLabel')} <span className="text-red-600 dark:text-red-300">*</span>
          </span>
          <Input
            value={amountStr}
            onChange={(e) => setAmountStr(e.target.value)}
            type="number"
            step="0.01"
            min="0.01"
            placeholder={t('credit_memo.apply.amountPlaceholder')}
            data-testid="credit-memo-apply-amount"
            aria-invalid={amountTooHigh}
          />
          {selectedInvoice ? (
            <p className="mt-1 text-xs text-pw-text-muted">
              {t('credit_memo.apply.maxHint', {
                max: formatUsd(maxApplyCents),
                remaining: formatUsd(memo.remaining_usd_cents),
                outstanding: formatUsd(selectedInvoice.outstanding_usd_cents),
              })}
            </p>
          ) : null}
          {amountTooHigh ? (
            <p className="mt-1 text-xs text-red-600 dark:text-red-300">
              {t('credit_memo.apply.amountTooHigh', { max: formatUsd(maxApplyCents) })}
            </p>
          ) : null}
        </label>

        {error ? (
          <p
            className="text-sm text-red-600 dark:text-red-300"
            data-testid="credit-memo-apply-error"
            role="alert"
          >
            {error}
          </p>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('credit_memo.actions.cancel')}
          </Button>
          <Button
            type="submit"
            disabled={submitDisabled}
            data-testid="credit-memo-apply-submit"
          >
            {submitting ? t('credit_memo.apply.submitting') : t('credit_memo.actions.applyShort')}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

export default ApplyCreditMemoModal;
